<script setup lang="ts">
  import { ref, computed, watch, onMounted, onUnmounted } from "vue";
  import { useRoute, useRouter } from "vue-router";
  import { storeToRefs } from "pinia";
  import { useForm } from "vee-validate";
  import { toTypedSchema } from "@vee-validate/zod";
  import { z } from "zod";
  import moment from "moment";
  import { Loader2 } from "lucide-vue-next";
  import OnePanePage from "@/templates/OnePanePage.vue";
  import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
  import { Button } from "@/components/ui/button";
  import { Accordion } from "@/components/ui/accordion";
  import { useJcdCampaignsStore } from "@stores/jcdCampaigns";
  import { useToastsStore } from "@stores/toastsStore";
  import JcdCampaignCampaignSection from "./JcdCampaignCampaignSection.vue";
  import JcdCampaignQuestionnaireSection from "./JcdCampaignQuestionnaireSection.vue";
  import JcdCampaignKpiSection from "./JcdCampaignKpiSection.vue";
  import type { JcdCampaign } from "@/types";

  const route = useRoute();
  const router = useRouter();
  const store = useJcdCampaignsStore();
  const toast = useToastsStore();
  const { currentCampaign, referentials, benchmarks, isNewCampaign, hasCampaignChanged } = storeToRefs(store);

  const campaignId = computed(() => (route.params.id ? Number(route.params.id) : null));

  const isReady = ref(false);
  const openSections = ref(["campaign", "questionnaire", "kpi"]);

  const headerTitle = computed(() => {
    if (isNewCampaign.value) return "Nouvelle campagne";
    const campaign = currentCampaign.value;
    return campaign?.brand ? `${campaign.brand} — S${campaign.week} ${campaign.year}` : "…";
  });

  const validationSchema = toTypedSchema(
    z.object({
      brand: z.string({ required_error: "La marque est obligatoire" }).min(1, "La marque est obligatoire"),
      logo_url: z.string().nullish(),
      year: z.number({ required_error: "L'année est obligatoire" }).int(),
      week: z.number({ required_error: "La semaine est obligatoire" }).int().min(1, "1 à 53").max(53, "1 à 53"),
      starts_on: z.string({ required_error: "La date de début est obligatoire" }).min(1, "La date de début est obligatoire"),
      ends_on: z.string({ required_error: "La date de fin est obligatoire" }).min(1, "La date de fin est obligatoire"),
      family: z.string().nullish(),
      family_class: z.string().nullish(),
      support: z.string().nullish(),
      level: z.string().nullish(),
      gross_budget: z.number().int().min(0).nullish(),
      faces_count: z.number().int().min(0).nullish(),
      visuals_count: z.number().int().min(0).nullish(),
      networks: z.array(z.string()),
      formats: z.array(z.string()),
      mosaic_code: z.string().nullish(),
      masked_visual_urls: z.array(z.string()),
      unmasked_visual_urls: z.array(z.string()),
      attribution_enabled: z.boolean(),
      competitor_brands: z.array(z.string()),
      intents: z
        .array(z.string())
        .refine(
          (intents) => intents.every((intent) => intent.length <= (referentials.value?.intent_max_length ?? Infinity)),
          "Une intention est trop longue pour le rapport PPT",
        ),
      agglomeration_mode: z.enum(["all", "only", "except"]),
      jcd_agglomeration_ids: z.array(z.number()),
      jcd_benchmark_id: z.number().nullish(),
      reco_benchmark: z.number().min(0, "0 à 100").max(100, "0 à 100").nullish(),
      attribution_benchmark: z.number().min(0, "0 à 100").max(100, "0 à 100").nullish(),
      intention_benchmark: z.number().min(0, "0 à 100").max(100, "0 à 100").nullish(),
    })
    .superRefine((campaign, ctx) => {
      if (campaign.agglomeration_mode === "all" || campaign.jcd_agglomeration_ids.length) return;
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["jcd_agglomeration_ids"],
        message: "Sélectionnez au moins une agglomération",
      });
    }),
  );

  const emptyValues = {
    brand: "",
    logo_url: null,
    year: undefined,
    week: undefined,
    starts_on: undefined,
    ends_on: undefined,
    family: null,
    family_class: null,
    support: null,
    level: null,
    gross_budget: null,
    faces_count: null,
    visuals_count: null,
    networks: [],
    formats: [],
    mosaic_code: null,
    masked_visual_urls: [],
    unmasked_visual_urls: [],
    attribution_enabled: true,
    competitor_brands: [],
    intents: [],
    agglomeration_mode: "all" as const,
    jcd_agglomeration_ids: [],
    jcd_benchmark_id: null,
    reco_benchmark: null,
    attribution_benchmark: null,
    intention_benchmark: null,
  };

  const { handleSubmit, meta, values, setFieldValue, resetForm } = useForm({
    validationSchema,
    validateOnMount: false,
    initialValues: emptyValues,
    keepValuesOnUnmount: true,
  });

  const hasErrors = computed(() => meta.value.dirty && !meta.value.valid);

  const toFormValues = (campaign: Partial<JcdCampaign>) => ({
    ...emptyValues,
    brand: campaign.brand ?? "",
    logo_url: campaign.logo_url,
    year: campaign.year,
    week: campaign.week,
    starts_on: campaign.starts_on,
    ends_on: campaign.ends_on,
    family: campaign.family,
    family_class: campaign.family_class,
    support: campaign.support,
    level: campaign.level,
    gross_budget: campaign.gross_budget,
    faces_count: campaign.faces_count,
    visuals_count: campaign.visuals_count,
    networks: [...(campaign.networks || [])],
    formats: [...(campaign.formats || [])],
    mosaic_code: campaign.mosaic_code,
    masked_visual_urls: [...(campaign.masked_visual_urls || [])],
    unmasked_visual_urls: [...(campaign.unmasked_visual_urls || [])],
    attribution_enabled: campaign.attribution_enabled,
    competitor_brands: [...(campaign.competitor_brands || [])],
    intents: [...(campaign.intents || [])],
    agglomeration_mode: campaign.agglomeration_mode ?? "all",
    jcd_agglomeration_ids: [...(campaign.jcd_agglomeration_ids || [])],
    jcd_benchmark_id: campaign.jcd_benchmark_id,
    reco_benchmark: campaign.reco_benchmark,
    attribution_benchmark: campaign.attribution_benchmark,
    intention_benchmark: campaign.intention_benchmark,
  });

  let syncingFromServer = false;

  onMounted(async () => {
    const loaders: Promise<unknown>[] = [store.fetchReferentials(), store.fetchBenchmarks()];
    if (campaignId.value) loaders.push(store.fetchOne(campaignId.value));
    else store.initCampaign();
    try {
      await Promise.all(loaders);
    } catch {
      toast.error("Campagne introuvable");
      router.push("/jcd-campaigns");
      return;
    }
    syncingFromServer = true;
    resetForm({ values: toFormValues(currentCampaign.value) });
    syncingFromServer = false;
    isReady.value = true;
  });

  onUnmounted(() => {
    currentCampaign.value = {};
  });

  watch(values, (v) => Object.assign(currentCampaign.value, v));

  watch(
    () => [values.year, values.week],
    ([year, week]) => {
      if (syncingFromServer || !year || !week) return;
      const weeksCount = referentials.value?.weeks_by_year[year as number];
      if (weeksCount && (week as number) > weeksCount) {
        setFieldValue("week", undefined);
        return;
      }
      const wednesday = moment().isoWeekYear(year as number).isoWeek(week as number).isoWeekday(3);
      setFieldValue("starts_on", wednesday.format("YYYY-MM-DD"));
      setFieldValue("ends_on", wednesday.clone().add(6, "days").format("YYYY-MM-DD"));
    },
  );

  watch(
    () => values.family,
    (family) => {
      if (syncingFromServer) return;
      const classes = (referentials.value?.families || {})[family as string] || [];
      if (values.family_class && !classes.includes(values.family_class)) setFieldValue("family_class", null);
    },
  );

  const onSubmit = handleSubmit(async () => {
    const saved = isNewCampaign.value ? await store.create() : await store.update();
    if (saved) router.push("/jcd-campaigns");
  });
</script>

<template>
  <OnePanePage>
    <Card class="mx-auto max-w-3xl p-6">
      <CardHeader class="pb-6">
        <CardTitle class="heading-page">{{ headerTitle }}</CardTitle>
      </CardHeader>
      <CardContent v-if="isReady">
        <Accordion
          v-model="openSections"
          type="multiple">
          <JcdCampaignCampaignSection :referentials="referentials" />
          <JcdCampaignQuestionnaireSection :referentials="referentials" />
          <JcdCampaignKpiSection
            :campaign="currentCampaign"
            :benchmarks="benchmarks || []"
            :show-results="!isNewCampaign" />
        </Accordion>
        <div class="pt-6">
          <Button
            type="button"
            :variant="hasErrors ? 'destructive' : 'default'"
            :disabled="!hasCampaignChanged || hasErrors"
            @click="onSubmit">
            Enregistrer
          </Button>
        </div>
      </CardContent>
      <CardContent v-else>
        <div class="text-muted-foreground flex items-center gap-2 text-sm">
          <Loader2 class="h-4 w-4 animate-spin" />
          Chargement…
        </div>
      </CardContent>
    </Card>
  </OnePanePage>
</template>
